import React from "react";

const PrivacyPolicy: React.FC = () => {
    return (
        <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
            <h1 className="text-3xl font-bold mb-4">Privacy Policy for UNILinkd</h1>

            <p className="mb-6 text-sm text-gray-500">
                Effective Date: 01/05/2026
            </p>

            <p className="mb-6">
                At <strong>UNILinkd</strong>, we take your privacy seriously. This Privacy
                Policy explains what information we collect, how we use it, and how we
                protect it. UNILinkd is designed exclusively for educational institutions
                and is used only by verified individuals within each institution.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                🗂️ What We Collect and Store
            </h2>

            <p className="mb-4">
                We collect and store only the minimum data required to operate the platform:
            </p>

            <ul className="list-disc ml-6 mb-4">
                <li className="mb-2">
                    <strong>Account Information:</strong> Email address and institutional credentials, used solely for authentication and identity verification within your institution.
                </li>
                <li className="mb-2">
                    <strong>Messages and Chat Data:</strong> To ensure reliable message delivery across devices — including on battery-optimized devices where background delivery via push notifications may be restricted — messages are temporarily stored on our servers in an encrypted format. This data is automatically and permanently deleted within 3–4 days of being delivered. It is never used for any purpose beyond delivery.
                </li>
                <li className="mb-2">
                    <strong>Shared Media and Files:</strong> Documents, PDFs, images, videos, and audio files intentionally shared between users within the institution. These are stored securely and accessible only to authorized users within the same institution.
                </li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                📅 Google Calendar Integration
            </h2>

            <p className="mb-4">
                UNILinkd offers an optional Google Calendar integration that allows users to generate Google Meet links directly within the platform for scheduling meetings and sessions.
            </p>

            <ul className="list-disc ml-6 mb-6">
                <li className="mb-2">This feature requires users to authenticate via Google OAuth.</li>
                <li className="mb-2">We request access only to the <strong>Google Calendar scope</strong> — the minimum required to create calendar events and generate Meet links.</li>
                <li className="mb-2">We do not read, store, or process any existing calendar events or personal calendar data.</li>
                <li className="mb-2">Google OAuth access is requested only at the time of use and is not stored persistently on our servers.</li>
                <li className="mb-2">This integration is entirely optional. Users who do not use this feature are not affected.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                🔐 Information We Do NOT Collect
            </h2>

            <p>We do not collect or store:</p>

            <ul className="list-disc ml-6 mb-6">
                <li>Contact lists or device phonebooks</li>
                <li>Device identifiers or hardware information</li>
                <li>Location data</li>
                <li>Browsing history or behavioral tracking data</li>
                <li>Any data beyond what is explicitly described in this policy</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                🚫 No Third-Party Access
            </h2>

            <ul className="list-disc ml-6 mb-6">
                <li>We do not use third-party analytics, advertising SDKs, or tracking tools.</li>
                <li>We do not share any user data with third parties, except as required by law.</li>
                <li>We do not use cookies or web beacons within the app.</li>
                <li>Google OAuth tokens are used solely to perform the requested Calendar action and are not shared or sold.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                🔐 Data Security
            </h2>

            <p className="mb-4">We follow best practices to ensure all stored data is:</p>

            <ul className="list-disc ml-6 mb-6">
                <li>Encrypted in transit and at rest</li>
                <li>Accessible only to authorized users within the same institution</li>
                <li>Automatically purged according to defined retention schedules (messages: 3–4 days; media: per institutional policy)</li>
                <li>Never accessible to UNILinkd staff except where required to resolve a technical issue, and only with institutional awareness</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                📱 App Permissions
            </h2>

            <p className="mb-4">UNILinkd may request the following device permissions:</p>

            <ul className="list-disc ml-6 mb-6">
                <li>Device storage — for accessing and sharing media files within the app</li>
                <li>Network access — for synchronization and message delivery</li>
                <li>Notification access — for message and activity alerts</li>
                <li>Camera and microphone — only when explicitly initiating media sharing or meetings</li>
            </ul>

            <p className="mb-6">
                These permissions are used only for functional purposes and are never used for data collection or tracking.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                👨‍🏫 Institutional Responsibility
            </h2>

            <p className="mb-4">
                Since UNILinkd operates exclusively within educational institutions, each institution is responsible for:
            </p>

            <ul className="list-disc ml-6 mb-6">
                <li>Onboarding and offboarding users</li>
                <li>Managing access roles and permissions</li>
                <li>Enforcing internal usage policies</li>
                <li>Ensuring their use of UNILinkd complies with applicable data protection regulations in their jurisdiction</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                📧 Contact Us
            </h2>

            <p className="mb-6">
                If you have any questions or concerns regarding this privacy policy or how your data is handled, please contact us at:
            </p>

            <p className="font-medium mb-6">
                support@unilinkd.com
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-3">
                📌 Changes to This Policy
            </h2>

            <p className="mb-6">
                We may update this policy from time to time. All changes will be reflected on this page with a updated "Effective Date". Continued use of UNILinkd following any changes constitutes acceptance of the revised policy.
            </p>

            <div className="text-sm text-gray-400 mt-12">
                © 2026 UNILinkd
            </div>
        </div>
    );
};

export default PrivacyPolicy;